import { type ChangeEvent, type DragEvent, useRef, useState } from "react";
import { CsvImportType } from "@forgepoint/shared";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Check,
  Download,
  FileText,
  Loader2,
  RotateCcw,
  Upload,
  X,
} from "lucide-react";
import { useCsvImport } from "./useCsvImport";
import AnimatedCounter from "./AnimatedCounter";
import ErrorMessage from "./ErrorMessage";
import StepIndicator from "./StepIndicator";
import { formatFileSize } from "@/lib/utils";

const IMPORT_TYPE_LABELS: Record<CsvImportType, string> = {
  ITEMS: "Items",
  PRICE_LIST_LINES: "Price List Lines",
  COMPANIES: "Companies",
  EXCHANGE_RATES: "Exchange Rates",
  OPERATIONS: "Operations",
  USERS: "Users",
};

const IMPORT_TYPE_HINTS: Record<CsvImportType, string> = {
  ITEMS:
    "Matched by item_name. Existing items will be overwritten with values in the CSV.",
  PRICE_LIST_LINES:
    "Matched by price_list_name + item_name. Missing price lists are auto-created. Items must exist first — import items before price list lines.",
  COMPANIES: "Matched and updated by company name.",
  EXCHANGE_RATES:
    "Each row is from,to,rate (e.g. USD,CAD,1.36). Each import replaces the active rate table.",
  OPERATIONS:
    "Matched by operation_name. Existing operations will be overwritten with values in the CSV.",
  USERS:
    "Emails already in the organization fail the import. role is a role slug like admin or user. invite_to_dashboard true sends the invite; admins are always invited.",
};

const stageVariants = {
  enter: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

interface CsvImportSheetProps {
  importType: CsvImportType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete: () => void;
}

/**
 * Sheet panel for uploading and tracking a CSV import.
 * Handles file selection, S3 upload, import job creation, and polling for completion.
 * @param importType - The entity type being imported
 * @param open - Whether the sheet is open
 * @param onOpenChange - Callback to control sheet visibility
 * @param onImportComplete - Called when the import finishes successfully; use to refresh the data table
 */
export default function CsvImportSheet({
  importType,
  open,
  onOpenChange,
  onImportComplete,
}: CsvImportSheetProps) {
  const { stage, summary, errorMessage, startImport, downloadTemplate, reset } =
    useCsvImport(importType);

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragCounterRef = useRef(0);
  const isProcessing = stage === "uploading" || stage === "processing";

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && isProcessing) {
      return;
    }
    if (!nextOpen) {
      reset();
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
    onOpenChange(nextOpen);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDragEnter = (e: DragEvent) => {
    e.preventDefault();
    dragCounterRef.current += 1;
    setIsDragging(true);
  };

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
  };

  const handleDragLeave = () => {
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: DragEvent) => {
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      return;
    }
    await startImport(selectedFile);
  };

  const handleDone = () => {
    onImportComplete();
    handleOpenChange(false);
  };

  const handleTryAgain = () => {
    reset();
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const label = IMPORT_TYPE_LABELS[importType];
  const hint = IMPORT_TYPE_HINTS[importType];

  return (
    <MotionConfig reducedMotion="user">
      <Sheet open={open} onOpenChange={handleOpenChange}>
        <SheetContent className="w-full sm:max-w-lg flex flex-col gap-0 p-0 overflow-hidden bg-stone-50">
          {/* Header */}
          <div className="px-6 pt-8 pb-5 bg-white border-b border-stone-200">
            <p className="text-[10px] tracking-widest uppercase font-semibold text-primary mb-1">
              CSV Import
            </p>
            <h2 className="text-xl font-semibold text-stone-900 tracking-tight pr-8">
              {label}
            </h2>
          </div>

          {/* Step Indicator */}
          <div className="px-6 py-4 bg-white border-b border-stone-200">
            <StepIndicator stage={stage} />
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-6">
            <AnimatePresence mode="wait">
              {stage === "idle" && (
                <motion.div
                  key="idle"
                  variants={stageVariants}
                  initial="enter"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm text-stone-500 italic leading-relaxed">
                      {hint}
                    </p>
                    <button
                      onClick={downloadTemplate}
                      className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 font-semibold shrink-0 underline underline-offset-2 transition-colors"
                    >
                      <Download className="h-3 w-3" />
                      Template
                    </button>
                  </div>

                  <div
                    role="button"
                    tabIndex={0}
                    aria-label="Upload CSV file"
                    className={`border rounded-lg cursor-pointer transition-all ${
                      isDragging
                        ? "border-primary/40 bg-primary/5"
                        : selectedFile
                          ? "border-stone-300 bg-stone-50"
                          : "border-stone-200 bg-white hover:border-stone-300"
                    }`}
                    onClick={() => fileInputRef.current?.click()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        fileInputRef.current?.click();
                      }
                    }}
                    onDragEnter={handleDragEnter}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                  >
                    <AnimatePresence mode="wait">
                      {selectedFile ? (
                        <motion.div
                          key="file-selected"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                          className="flex items-center gap-4 p-4"
                        >
                          <div className="p-2.5 bg-stone-900 rounded-md shrink-0">
                            <FileText className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-stone-900 truncate">
                              {selectedFile.name}
                            </p>
                            <p className="text-xs text-stone-400 mt-0.5">
                              {formatFileSize(selectedFile.size)} · Click to
                              replace
                            </p>
                          </div>
                          <div className="w-2 h-2 rounded-full bg-primary shrink-0" />
                        </motion.div>
                      ) : (
                        <motion.div
                          key="file-empty"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ duration: 0.15 }}
                          className="flex flex-col items-center justify-center gap-3 py-12"
                        >
                          <div
                            className={`p-3 rounded-md transition-colors ${
                              isDragging ? "bg-primary/10" : "bg-stone-100"
                            }`}
                          >
                            <Upload
                              className={`h-5 w-5 transition-colors ${
                                isDragging ? "text-primary" : "text-stone-400"
                              }`}
                            />
                          </div>
                          <div className="text-center">
                            <p className="text-sm font-medium text-stone-700">
                              Drop a CSV here
                            </p>
                            <p className="text-xs text-stone-400 mt-0.5">
                              or click to browse
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </motion.div>
              )}

              {isProcessing && (
                <motion.div
                  key="processing"
                  variants={stageVariants}
                  initial="enter"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.2 }}
                  className="flex flex-col items-center justify-center py-16 gap-5 text-center"
                >
                  <div className="w-14 h-14 rounded-full border border-stone-200 flex items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-stone-700" />
                  </div>
                  <div className="space-y-1.5">
                    <p className="font-medium text-stone-900">
                      {stage === "uploading"
                        ? "Uploading file"
                        : "Processing import"}
                    </p>
                    <p className="text-sm text-stone-500 max-w-xs">
                      Please wait while your import is being processed.
                    </p>
                  </div>
                </motion.div>
              )}

              {stage === "complete" && summary !== null && (
                <motion.div
                  key="complete"
                  variants={stageVariants}
                  initial="enter"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.2 }}
                  className="space-y-5"
                >
                  <div className="flex items-center gap-3">
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 20,
                        delay: 0.05,
                      }}
                      className="p-1.5 bg-emerald-100 rounded-full shrink-0"
                    >
                      <Check className="h-4 w-4 text-emerald-600" />
                    </motion.div>
                    <div>
                      <p className="font-semibold text-stone-900">
                        Import complete
                      </p>
                      <p className="text-xs text-stone-500">
                        {summary.totalRows} rows processed
                      </p>
                    </div>
                  </div>

                  <motion.div
                    className="grid grid-cols-2 gap-3"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15, duration: 0.2 }}
                  >
                    <div className="bg-white border border-stone-200 rounded-lg p-4">
                      <p className="text-3xl font-bold text-stone-900 tabular-nums">
                        <AnimatedCounter value={summary.createdCount} />
                      </p>
                      <p className="text-xs text-stone-400 uppercase tracking-widest font-semibold mt-2">
                        Created
                      </p>
                    </div>
                    <div className="bg-white border border-stone-200 rounded-lg p-4">
                      <p className="text-3xl font-bold text-stone-900 tabular-nums">
                        <AnimatedCounter value={summary.updatedCount} />
                      </p>
                      <p className="text-xs text-stone-400 uppercase tracking-widest font-semibold mt-2">
                        Updated
                      </p>
                    </div>
                  </motion.div>
                </motion.div>
              )}

              {stage === "failed" && (
                <motion.div
                  key="failed"
                  variants={stageVariants}
                  initial="enter"
                  animate="visible"
                  exit="exit"
                  transition={{ duration: 0.2 }}
                  className="space-y-4"
                >
                  <div className="flex items-center gap-3">
                    <motion.div
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 260,
                        damping: 20,
                      }}
                      className="p-1.5 bg-red-100 rounded-full shrink-0"
                    >
                      <X className="h-4 w-4 text-red-600" />
                    </motion.div>
                    <div>
                      <p className="font-semibold text-stone-900">
                        Import failed
                      </p>
                      <p className="text-xs text-stone-500">
                        Review the error and try again
                      </p>
                    </div>
                  </div>

                  {errorMessage && <ErrorMessage message={errorMessage} />}
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-white border-t border-stone-200 flex justify-end gap-2">
            {stage === "idle" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleImport}
                  disabled={!selectedFile}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  Import
                </Button>
              </>
            )}

            {stage === "complete" && (
              <Button
                onClick={handleDone}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
              >
                Done
              </Button>
            )}

            {stage === "failed" && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleTryAgain}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                  Try Again
                </Button>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </MotionConfig>
  );
}
